import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';
import StaggerReveal from '../../../components/feature/StaggerReveal';
import { useState, useEffect } from 'react';

const WHATSAPP_URL = 'https://wa.me/18495388899?text=Hola%20InnoFlex!%20Me%20interesa%20el%20sistema%20ERP%2C%20POS%20y%20Contabilidad.';
const PRICING_URL = '#pricing';

interface ModuleData {
  icon: string;
  titleKey: string;
  subKey: string;
  accentHex: string;
  featureKeys: string[];
}

const MODULES: ModuleData[] = [
  {
    icon: 'ri-shopping-cart-2-line',
    titleKey: 'sw_mod_pos_title',
    subKey: 'sw_mod_pos_sub',
    accentHex: '#00cfff',
    featureKeys: ['sw_mod_pos_f1', 'sw_mod_pos_f2', 'sw_mod_pos_f3', 'sw_mod_pos_f4'],
  },
  {
    icon: 'ri-building-3-line',
    titleKey: 'sw_mod_erp_title',
    subKey: 'sw_mod_erp_sub',
    accentHex: '#00ff9d',
    featureKeys: ['sw_mod_erp_f1', 'sw_mod_erp_f2', 'sw_mod_erp_f3', 'sw_mod_erp_f4'],
  },
  {
    icon: 'ri-line-chart-line',
    titleKey: 'sw_mod_conta_title',
    subKey: 'sw_mod_conta_sub',
    accentHex: '#facc15',
    featureKeys: ['sw_mod_conta_f1', 'sw_mod_conta_f2', 'sw_mod_conta_f3', 'sw_mod_conta_f4'],
  },
  {
    icon: 'ri-file-text-line',
    titleKey: 'sw_mod_fel_title',
    subKey: 'sw_mod_fel_sub',
    accentHex: '#e53535',
    featureKeys: ['sw_mod_fel_f1', 'sw_mod_fel_f2', 'sw_mod_fel_f3', 'sw_mod_fel_f4'],
  },
];

interface KeywordChip {
  labelKey: string;
  icon: string;
  accent: string;
}

const KEYWORDS: KeywordChip[] = [
  { labelKey: 'sw_keyword_erp',     icon: 'ri-building-3-line',      accent: '#00ff9d' },
  { labelKey: 'sw_keyword_pos',     icon: 'ri-shopping-cart-2-line', accent: '#00cfff' },
  { labelKey: 'sw_keyword_conta',   icon: 'ri-line-chart-line',      accent: '#facc15' },
  { labelKey: 'sw_keyword_inv',     icon: 'ri-store-2-line',         accent: '#f97316' },
  { labelKey: 'sw_keyword_fel',     icon: 'ri-file-text-line',       accent: '#e53535' },
  { labelKey: 'sw_keyword_multi',   icon: 'ri-map-pin-2-line',       accent: '#a855f7' },
  { labelKey: 'sw_keyword_offline', icon: 'ri-wifi-off-line',        accent: '#00cfff' },
];

interface ModuleCardProps {
  mod: ModuleData;
  isLight: boolean;
}

function ModuleCard({ mod, isLight }: ModuleCardProps) {
  const { t } = useTranslation();

  const cardBg    = isLight ? '#ffffff' : 'rgba(10,20,40,0.60)';
  const textMain  = isLight ? '#0a1628' : '#ffffff';
  const textSub   = isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.50)';

  return (
    <div
      className="relative rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1"
      style={{
        backgroundColor: cardBg,
        border: `1px solid ${mod.accentHex}28`,
      }}
    >
      {/* Top accent bar */}
      <div
        className="h-1 w-full flex-shrink-0"
        style={{ background: `linear-gradient(to right, ${mod.accentHex}, ${mod.accentHex}30)` }}
      />

      <div className="p-5 sm:p-6 flex flex-col flex-1 gap-3">
        {/* Icon + Title */}
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
            style={{ backgroundColor: `${mod.accentHex}18`, color: mod.accentHex }}
          >
            <i className={`${mod.icon} text-lg`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="font-heading text-base font-bold leading-snug mb-0.5"
              style={{ color: textMain }}
            >
              {t(mod.titleKey)}
            </h3>
            <p className="font-body text-xs leading-snug" style={{ color: `${mod.accentHex}cc` }}>
              {t(mod.subKey)}
            </p>
          </div>
        </div>

        {/* Features */}
        <ul className="flex flex-col gap-2 flex-1">
          {mod.featureKeys.map((key) => (
            <li key={key} className="flex items-start gap-2">
              <div
                className="w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                style={{ backgroundColor: `${mod.accentHex}18`, color: mod.accentHex }}
              >
                <i className="ri-check-line text-xs" />
              </div>
              <span className="font-body text-xs leading-relaxed" style={{ color: textSub }}>
                {t(key)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   CyclingText — cycles through words with a smooth slide-up/fade animation
───────────────────────────────────────────────────────────────────────── */
interface CyclingTextProps {
  words: string[];
  interval?: number;
}

function CyclingText({ words, interval = 2400 }: CyclingTextProps) {
  const [index, setIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setIndex((i) => (i + 1) % words.length);
        setAnimating(false);
      }, 320);
    }, interval);
    return () => clearInterval(timer);
  }, [words.length, interval]);

  return (
    <span
      className="inline-block relative"
      style={{
        background: 'linear-gradient(90deg, #00cfff 0%, #00ff9d 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        opacity: animating ? 0 : 1,
        transform: animating ? 'translateY(-10px)' : 'translateY(0px)',
        transition: 'opacity 0.28s ease, transform 0.28s ease',
        minWidth: '8ch',
      }}
    >
      {words[index]}
    </span>
  );
}

export default function Services() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { t } = useTranslation();

  const sectionBg = isLight ? '#f7fbff' : '#020817';
  const textMain  = isLight ? '#0a1628' : '#ffffff';
  const textSub   = isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.50)';

  const cyclingWords = [
    t('sw_title_2'),
    t('sw_cycle_2'),
    t('sw_cycle_3'),
  ];

  return (
    <section
      id="services"
      className="py-16 md:py-28 px-4 sm:px-5 md:px-6"
      style={{ background: sectionBg }}
    >
      <div className="max-w-7xl mx-auto">

        {/* ── Header ───────────────────────────────────────────── */}
        <div className="text-center mb-8 md:mb-12 px-2 sm:px-0">
          <span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-3">
            {t('sw_label')}
          </span>
          <h2
            className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight"
            style={{ color: textMain }}
          >
            {t('sw_title_1')}{' '}
            <span className="text-electric">{t('sw_title_accent')}</span>
            <br className="hidden sm:block" />
            <span className="text-2xl sm:text-3xl md:text-4xl font-semibold" style={{ color: textSub }}>
              <CyclingText words={cyclingWords} interval={2600} />
            </span>
          </h2>
          <p
            className="font-body text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
            style={{ color: textSub }}
          >
            {t('sw_sub')}
          </p>
        </div>

        {/* ── Keyword Pills (very prominent) ───────────────────── */}
        <div className="flex flex-wrap justify-center gap-2.5 md:gap-3 mb-10 md:mb-14 px-2">
          {KEYWORDS.map((kw) => (
            <div
              key={kw.labelKey}
              className="flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full font-heading font-bold text-sm md:text-base transition-all duration-200 hover:scale-105 cursor-default"
              style={{
                backgroundColor: `${kw.accent}15`,
                border: `1.5px solid ${kw.accent}55`,
                color: kw.accent,
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${kw.icon} text-sm md:text-base`} />
              </div>
              {t(kw.labelKey)}
            </div>
          ))}
        </div>

        {/* ── 4 Module Cards ───────────────────────────────────── */}
        <StaggerReveal
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-10 md:mb-14"
          staggerDelay={100}
        >
          {MODULES.map((mod) => (
            <ModuleCard key={mod.titleKey} mod={mod} isLight={isLight} />
          ))}
        </StaggerReveal>

        {/* ── CTA Strip ────────────────────────────────────────── */}
        <div
          className="relative rounded-2xl overflow-hidden border"
          style={{
            background: isLight
              ? 'linear-gradient(135deg, rgba(0,207,255,0.06) 0%, rgba(0,255,157,0.04) 100%)'
              : 'linear-gradient(135deg, rgba(0,207,255,0.09) 0%, rgba(0,255,157,0.05) 100%)',
            borderColor: isLight ? 'rgba(0,207,255,0.25)' : 'rgba(0,207,255,0.18)',
          }}
        >
          {/* BG glows */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div
              className="absolute -top-16 -right-16 w-64 h-64 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(0,207,255,0.14) 0%, transparent 70%)' }}
            />
            <div
              className="absolute -bottom-10 left-12 w-48 h-48 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(0,255,157,0.10) 0%, transparent 70%)' }}
            />
          </div>

          <div className="relative z-10 p-6 sm:p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-5 md:gap-6">
            <div className="w-full md:max-w-xl text-center md:text-left">
              <h3
                className="font-heading text-xl md:text-2xl font-bold mb-2"
                style={{ color: textMain }}
              >
                {t('sw_cta_heading')}
              </h3>
              <p className="font-body text-sm leading-relaxed" style={{ color: textSub }}>
                {t('sw_cta_desc')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto flex-shrink-0">
              <a
                href={PRICING_URL}
                className="flex items-center justify-center gap-2.5 bg-electric hover:bg-electric/85 text-[#020817] font-heading font-bold text-sm px-6 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 w-full sm:w-auto"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-price-tag-3-line text-base" />
                </div>
                {t('sw_cta_plans')}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1db954] text-white font-heading font-bold text-sm px-6 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 w-full sm:w-auto"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line text-base" />
                </div>
                {t('sw_cta_ask')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
